import {
  CHAIN_IDS,
  SUPPORTED_CHAIN_IDS_LIMIT_ORDERS,
  type LimitOrderSupportedChainId,
} from '@/constants/chain';

export const ADVANCED_ORDER_PROVIDERS = ['1inch', 'definitive'] as const;
export type AdvancedOrderProvider = (typeof ADVANCED_ORDER_PROVIDERS)[number];
export type AdvancedOrderProviderOverrides = Readonly<
  Partial<Record<LimitOrderSupportedChainId, AdvancedOrderProvider>>
>;

export const ADVANCED_ORDER_TYPES = ['limit', 'stop-loss', 'take-profit'] as const;
export type AdvancedOrderType = (typeof ADVANCED_ORDER_TYPES)[number];
export type TriggerOrderType = Exclude<AdvancedOrderType, 'limit'>;

export const ADVANCED_ORDER_TYPE_LABELS = {
  'limit': 'Limit',
  'stop-loss': 'Stop-Loss',
  'take-profit': 'Take-Profit',
} as const satisfies Record<AdvancedOrderType, string>;

export const ADVANCED_ORDER_PROVIDER_CAPABILITIES = {
  '1inch': ['limit'],
  'definitive': ['limit', 'stop-loss', 'take-profit'],
} as const satisfies Record<AdvancedOrderProvider, readonly AdvancedOrderType[]>;

// Rollout baseline for every supported chain without an explicit override.
export const DEFAULT_ADVANCED_ORDER_PROVIDER: AdvancedOrderProvider = 'definitive';
export const ADVANCED_ORDER_PROVIDER_OVERRIDES = {} satisfies AdvancedOrderProviderOverrides;

export function isLimitOrderSupportedChainId(
  chainId: number,
): chainId is LimitOrderSupportedChainId {
  return SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(chainId);
}

export function getAdvancedOrderProvider(
  chainId: LimitOrderSupportedChainId,
  overrides: AdvancedOrderProviderOverrides = ADVANCED_ORDER_PROVIDER_OVERRIDES,
): AdvancedOrderProvider {
  return overrides[chainId] ?? DEFAULT_ADVANCED_ORDER_PROVIDER;
}

export function isAdvancedOrderType(value: unknown): value is AdvancedOrderType {
  return typeof value === 'string' && (ADVANCED_ORDER_TYPES as readonly string[]).includes(value);
}

export function isTriggerOrderType(value: unknown): value is TriggerOrderType {
  return value === 'stop-loss' || value === 'take-profit';
}

export function isAdvancedOrderTypeSupported(
  chainId: number,
  orderType: AdvancedOrderType,
  overrides: AdvancedOrderProviderOverrides = ADVANCED_ORDER_PROVIDER_OVERRIDES,
): boolean {
  if (!isLimitOrderSupportedChainId(chainId)) return false;
  const capabilities: readonly AdvancedOrderType[] =
    ADVANCED_ORDER_PROVIDER_CAPABILITIES[getAdvancedOrderProvider(chainId, overrides)];
  return capabilities.includes(orderType);
}

const LIMIT_ORDER_PROTOCOL_ADDRESS_DEFAULT = '0x111111125421ca6dc452d289314280a0f8842a65';

/**
 * 1inch Aggregation Router v6 (Limit Order Protocol) is NOT at the canonical
 * address on every chain. Signing an order against the wrong address produces
 * an EIP-712 hash the 1inch orderbook rejects with ORDER_INVALID ("invalid
 * order hash"), and approvals/cancels sent there are no-ops. Keep this map in
 * sync with `getLimitOrderContract` from `@1inch/limit-order-sdk` (enforced by
 * tests/unit/constants/advancedOrder.test.ts).
 */
const LIMIT_ORDER_PROTOCOL_ADDRESS_OVERRIDES: Record<number, `0x${string}`> = {
  [CHAIN_IDS.ROBINHOOD]: '0x5a705de8982235a7fa45bb83dcacf03a211389c7',
};

export function getLimitOrderProtocolAddress(chainId: number): `0x${string}` {
  return LIMIT_ORDER_PROTOCOL_ADDRESS_OVERRIDES[chainId] ?? LIMIT_ORDER_PROTOCOL_ADDRESS_DEFAULT;
}

/**
 * Matcha's integrator address for the 1inch FeeTaker extension. Every limit
 * order built by `/api/orderbook/build-order` embeds this as the IntegratorFee
 * recipient, and `/api/orderbook/order` re-checks it on submission so the
 * protocol fee always flows back to Matcha.
 */
export const LIMIT_ORDER_FEE_COLLECTOR_ADDRESS = '0x9b858Be6E3047D88820f439B240deaC2418a2551';
